import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, shortDate, showMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExpandToggle } from "@/components/ExpandToggle";
import { ChevronDown, Copy, ExternalLink } from "lucide-react";

const REASONS = { telefono: "Stesso telefono", nome_citta: "Stesso nome e città" };
const FILTERS = [["tutti", "Tutti"], ["telefono", "Stesso telefono"], ["nome_citta", "Solo nome e città"]];

const Account = ({ c, onSelect }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 py-1 text-xs" data-testid={`dup-account-${c.id}`}>
    <div className="min-w-0">
      <button className="font-medium text-foreground hover:text-primary" onClick={() => onSelect(c.id)} data-testid={`dup-open-${c.id}`}>{c.name}</button>
      <span className="ml-2 text-muted-foreground">{c.email || "senza email"} · {c.phone || "senza telefono"} · {c.city || "—"}</span>
    </div>
    <span className="flex shrink-0 items-center gap-3 text-muted-foreground">
      <span>{num(c.orders_count)} {c.orders_count === 1 ? "ordine" : "ordini"}{showMoney() && ` · ${eur(c.total_spent)}`}{c.last_order_date ? ` · ultimo ${shortDate(c.last_order_date)}` : ""}</span>
      {c.shopify_url && <a href={c.shopify_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline" data-testid={`dup-shopify-${c.id}`}>Shopify<ExternalLink className="h-3 w-3" /></a>}
    </span>
  </div>
);

export const DuplicateCustomers = ({ onSelect }) => {
  const { data } = useQuery({ queryKey: ["customers", "duplicates"], queryFn: async () => (await api.get("/customers/duplicates")).data });
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("tutti");
  const [shown, setShown] = useState(false);
  if (!data) return null;
  const groups = data.groups.filter((g) => filter === "tutti" || (filter === "telefono" ? g.reasons.includes("telefono") : !g.reasons.includes("telefono")));
  return (
    <Card className="mb-6 p-5" data-testid="customers-duplicates">
      <button onClick={() => setShown(!shown)} className="flex w-full items-center justify-between gap-2 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="dup-section-toggle">
        <span className="flex items-center gap-1.5"><Copy className="h-3.5 w-3.5 text-primary" />Possibili account doppi · {num(data.total)} gruppi</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${shown ? "rotate-180" : ""}`} />
      </button>
      {shown && <div className="mt-2" data-testid="dup-section-body">
      <p className="mb-3 text-xs text-muted-foreground">Stessa persona con più account: falsano speso, fasce e clienti in calo. Uniscili da Shopify ({num(data.by_phone)} con lo stesso telefono, quasi certi).</p>
      <div className="mb-3 flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} className="h-7 rounded-full text-xs" onClick={() => setFilter(k)} data-testid={`dup-filter-${k}`}>{l}</Button>
        ))}
      </div>
      <div className="space-y-2">
        {(open ? groups : groups.slice(0, 3)).map((g) => (
          <div key={g.accounts[0].id} className="rounded-md border border-border/50 px-3 py-2" data-testid="dup-group">
            <div className="mb-1 flex gap-1">{g.reasons.map((r) => <Badge key={r} variant="outline" className="text-[10px]">{REASONS[r]}</Badge>)}</div>
            {g.accounts.map((c) => <Account key={c.id} c={c} onSelect={onSelect} />)}
          </div>
        ))}
        {!groups.length && <p className="text-sm text-muted-foreground" data-testid="dup-empty">Nessun doppione trovato</p>}
      </div>
      <ExpandToggle total={groups.length} open={open} onToggle={() => setOpen(!open)} limit={3} testid="dup-toggle" />
      </div>}
    </Card>
  );
};
